import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { TOKEN } from 'features/auth/constants';
import { clearAuthToken, getAuthToken } from 'features/auth/helpers';
import {
  API_RESPONSE_CODE,
  API_RESPONSE_STATUS,
  HttpClientProps,
  TQueryConfig,
  TQueryError,
} from 'lib/axios/types';
import qs from 'qs';
import { getPath } from 'routes/router-paths';
import { messageUtils } from 'utils';
import { startLoadingSpinner, stopLoadingSpinner } from 'utils/loadingSpinner';
import { ObjectType } from 'utils/types';

axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig & TQueryConfig) => {
    const { disableSpinner } = config;
    if (!disableSpinner) {
      startLoadingSpinner();
    }
    return config;
  },
  (error: AxiosError) => {
    stopLoadingSpinner();
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    const { message } = messageUtils;

    stopLoadingSpinner();

    const { config, data } = response || {};

    const isUnauthorized = data?.status === API_RESPONSE_STATUS.UNAUTHORIZED;
    const isSuccess = data?.code === API_RESPONSE_CODE.SUCCESS;
    const isShowCustomErrorMsg = (config as any)?.manuallyHandlingErrorMsg;

    if (!isSuccess) {
      if (isUnauthorized) {
        void message.error('Token expired');
        clearAuthToken();
        window.location.href = getPath(
          'loginPage',
          qs.stringify({
            redirectedFrom: window.location.pathname,
          })
        );
      } else if (!isShowCustomErrorMsg) {
        console.log('ERROR: ', response);
        void message.error(
          data?.message ?? data?.title ?? 'Unknown error occurred'
        );
      }
    }

    return response;
  },

  async (error: AxiosError) => {
    const { message } = messageUtils;

    let hasCustomMessageContent = false;

    const { response, code, config } = error;
    const { manuallyHandlingErrorMsg } = config as AxiosError['config'] &
      TQueryConfig;

    stopLoadingSpinner();

    if (response?.status === 403) {
      window.location.href = getPath('portalPage');
    }

    if (response?.status === 408 || code === 'ECONNABORTED') {
      message.error('Request timed out.');
      hasCustomMessageContent = true;
    }

    if (response?.status === 401) {
      hasCustomMessageContent = true;
      message.error('Token expired!');
      clearAuthToken();
      window.location.href = getPath(
        'loginPage',
        qs.stringify({
          redirectedFrom: window.location.pathname,
        })
      );
    }

    // TODO: chỗ này làm sau
    // if (
    //   response?.status === 401 &&
    //   ![`${baseURL}/UserAuth/Authenticate`, '/UserAuth/Refresh'].includes(
    //     config?.url as string
    //   )
    // ) {
    //   try {
    //     return await handleRefreshToken(config as AxiosRequestConfig);
    //   } finally {
    //     stopLoadingSpinner();
    //   }
    // }

    if (!manuallyHandlingErrorMsg) {
      console.log('Error: ', response);
      !hasCustomMessageContent &&
        message.error(
          (response?.data as ObjectType)?.title ??
            (response?.data as ObjectType)?.message ??
            'Unknown error occurred'
        );
    }

    return Promise.reject(manuallyHandlingErrorMsg ? response?.data : error);
  }
);

export const axiosBaseQuery =
  ({ baseUrl } = { baseUrl: '' }) =>
  async (config: HttpClientProps & TQueryConfig) => {
    const {
      url,
      method,
      data,
      params,
      customHeader,
      manuallyHandlingErrorMsg,
      disableSpinner,
    } = config;

    const accessToken = getAuthToken(TOKEN.ACCESS_TOKEN);

    const axiosConfig = {
      url: baseUrl + url,
      method,
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        ...customHeader,
      },
      ...(params && { params }),
      ...(data && { data }),
      manuallyHandlingErrorMsg,
      disableSpinner,
    } as AxiosRequestConfig<ObjectType> & TQueryConfig;

    try {
      const result = await axios(axiosConfig);

      const { code, status, message, ...responseData } = (result?.data ||
        {}) as any;

      if (code !== API_RESPONSE_CODE.SUCCESS) {
        return {
          data: null,
          error: {
            status: status,
            errors: [],
            title: message,
          } as TQueryError,
        };
      }

      return {
        data: responseData,
        code,
        message,
      };
    } catch (err: any) {
      return {
        data: null,
        error: {
          status: err.response?.status,
          errors: err.response?.data?.errors || err.message,
          title: err.response?.data?.title || err.message,
        } as TQueryError,
      };
    }
  };
